import {
    Api, ArrayUtil, GeneralSettings, Keys, LogManager, MenuItem, Navigation, StringUtil, Styles
} from "@mcleod/core";
import { ImageName, ImageNameUtil } from "@mcleod/images";
import { Button, ClickListener, KeyEvent, Panel, PanelProps } from "@mcleod/components";
import { MainMenu } from "./MainMenu";
import { MenuLauncher } from "./MenuLauncher";
import { MenuFavorites } from "./MenuFavorites";

const log = LogManager.getLogger("common/PanelToolbox");

export class PanelToolbox extends Panel {
    private mainMenu: MainMenu;
    private panelToolboxImages: Panel;

    constructor(mainMenu: MainMenu, toolboxItems: MenuItem[], props?: Partial<PanelProps>) {
        super({id: "panelMenuToolbox", fillRow: true, ...props});
        this.mainMenu = mainMenu;
        this.panelToolboxImages = new Panel({ id: "panelMenuToolboxImageRow", rowBreakDefault: false });
        this.panelToolboxImages.addKeyDownListener((event: KeyEvent) => this.handleToolboxImageKey(event));
        this.add(this.panelToolboxImages);
        this.addToolboxButtons(toolboxItems);
    }

    addToolboxButtons(toolboxItems: MenuItem[]) {
        this.addHomeButton();
        toolboxItems?.forEach(toolboxItem => this.addToolboxButton(toolboxItem));
    }

    private addHomeButton() {
        const homeButton = this.createImageOnlyLauncher({
            path: "",
            caption: "Home",
            image: ImageName.home
        }, () => {
            Navigation.navigateTo("");
            this.mainMenu.hide();
        });
        this.panelToolboxImages.add(homeButton);
    }

    focusFirstToolboxItem() {
        this.panelToolboxImages.components[0]?.focus();
    }

    private handleToolboxImageKey(event: KeyEvent) {
        if (event.isKey(Keys.ARROW_DOWN, {})) {
            if (MenuFavorites.getFavoriteCount() > 0)
                this.mainMenu.selectFirstFavorite();
            else
                this.mainMenu.focus();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (event.isKey(Keys.ARROW_LEFT, {}) || event.isKey(Keys.ARROW_RIGHT, {})) {
            let index = 0;
            for (let x = 0; x < this.panelToolboxImages.components.length; x++) {
                if (this.panelToolboxImages.components[x].hasFocus()) {
                    index = x;
                    break;
                }
            }
            if (event.isKey(Keys.ARROW_LEFT, {}))
                index--;
            else if (event.isKey(Keys.ARROW_RIGHT, {}))
                index++;
            if (index < 0)
                index = 0;
            if (index > this.panelToolboxImages.components.length - 1)
                index = this.panelToolboxImages.components.length - 1;
            this.panelToolboxImages.components[index].focus();
        }
    }

    private addToolboxButton(toolboxItem: MenuItem){
        let menuItem = MainMenu.findMenuItemForPath(toolboxItem.path);
        if (menuItem != null) {
            if (toolboxItem.image != null && menuItem?.image == null) {
                log.debug("Using image name from settings menu item");
                menuItem = { ...menuItem, image: toolboxItem.image };
            }
            this.panelToolboxImages.add(this.createImageOnlyLauncher(menuItem));
        } else if (toolboxItem.path.startsWith("https")) {
            this.panelToolboxImages.add(this.createImageOnlyLauncher(toolboxItem));
        }
    }

    private createImageOnlyLauncher(item: MenuItem, clickListener?: ClickListener): Button {
        const openPageInSlideUp = !item?.path?.startsWith('https');
        const result = new Button({
            imageName: ImageNameUtil.fromString(item.image),
            tooltip: item.caption,
            rowBreak: false,
            imageHeight: 24,
            imageWidth: 24,
            padding: 4,
            margin: 0,
            color: "component.palette.menu.secondaryColor",
            borderWidth: 0
        });

        result.addFocusListener(() => {
            this.mainMenu.unselectAllMenus();
            result.backgroundColor = Styles.HOVER_WITHOUT_BACKGROUND_COLOR.backgroundColor;
        });
        result.addBlurListener(() => {
            result.backgroundColor = undefined;
            result.color = "primary.darker";
        });

        if (clickListener != null) {
            result.addClickListener(clickListener);
        } else if (item.caption === "Mpact.RatePro") {
            result.addClickListener(() => {
                Api.post("lme/dispatch/open-mpact-url").then((result) => {
                    const apiData = result.data[0];
                    if (apiData) {
                        window.open(apiData.encrypted_url, "_blank");
                    }
                });
            })
        } else if (item.path.startsWith("https")) {
            result.addClickListener(() => window.open(item.path, "", "popup"));
        } else {
            const clickListener = new MenuLauncher(this.mainMenu).openPageOnClick(item, openPageInSlideUp);
            result.addClickListener(clickListener);
        }
        return result;
    }
}
